import {
	Component,
	inject,
	output,
	input,
	Signal,
	signal,
	computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AiInfoDialogComponent } from '@core/dialogs/ai-info-dialog/ai-info-dialog.component';
import { AiImageTipsDialogComponent } from '@core/dialogs/ai-image-tips-dialog/ai-image-tips-dialog.component';
import {
	IAiAssetAnalysisResponse,
	ImageAnalysisService,
} from '@app/shared/services/image-analysis.service';
import { Subject, lastValueFrom } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { IAttachedFile } from '../../interfaces/files.interfaces';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { createFileMutation } from '@app/shared/ui-components/au-file-loader/au-file-loader.queries';
import { FileLoaderService } from '@app/shared/ui-components/au-file-loader/services/file-loader.service';
import { IClassification } from '../../interfaces/classification.interfaces';
import { cmmsClassifications } from '../../state/cmms.selectors';
import { Store } from '@ngrx/store';
import { IFileUploadEvent } from '@app/shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { TrackPendoService } from '@app/shared/services/track-pendo.service';

@Component({
	selector: 'au-ai-asset-image-analysis-card',
	standalone: true,
	imports: [
		CommonModule,
		MatButtonModule,
		MatChipsModule,
		MatIconModule,
		NgxTolgeeModule,
	],
	templateUrl: './ai-asset-image-analysis-card.component.html',
	styleUrls: ['./ai-asset-image-analysis-card.component.scss'],
})
export class AiAssetImageAnalysisCardComponent {
	analysisCompleted = output<IAiAssetAnalysisResponse>();
	isLoadingState = output<boolean>();
	existingData = input<ICmmsAsset>();
	attachedFiles = input<IAttachedFile[]>();
	filesAdded = output<IAttachedFile[]>();
	withButton = input<boolean>(false);

	private isCardToggled = signal(false);
	protected isCardVisible = computed(() =>
		this.withButton() ? this.isCardToggled() : true
	);

	private readonly dialog = inject(MatDialog);
	private readonly imageAnalysisService = inject(ImageAnalysisService);
	private readonly notify = inject(IconSnackBarService);
	private readonly fileLoaderService = inject(FileLoaderService);
	private readonly store = inject(Store);
	private readonly translateService = inject(TranslateService);
	private readonly trackPendoService = inject(TrackPendoService);
	private classifications: Signal<IClassification[]> =
		this.store.selectSignal(cmmsClassifications);

	protected toggleCardVisibility(): void {
		this.isCardToggled.set(!this.isCardToggled());
	}

	protected hasExistingImages(): boolean {
		if (!this.attachedFiles) {
			return false;
		}

		return this.attachedFiles().some(file =>
			file?.file?.contentType?.startsWith('image')
		);
	}

	async onFileSelected(event: Event) {
		const element = event.currentTarget as HTMLInputElement;
		const fileList: FileList = element.files;

		if (fileList && fileList.length > 0) {
			this.isLoadingState.emit(true);
			try {
				const fileUrls = await this.uploadFilesAndGetUrls(Array.from(fileList));
				this.performAnalysis(fileUrls, false);
			} catch (error) {
				this.isLoadingState.emit(false);
				console.error('File upload error:', error);
				this.notify.error(this.translateService.instant('file_upload_error'));
			}
		}
		this.trackPendoService.track('Asset image analysis - new');
	}

	private uploadFilesAndGetUrls(files: File[]): Promise<string[]> {
		const fileUploadPromises = files.map(file =>
			lastValueFrom(
				this.fileLoaderService.upload({
					file: file,
					index: 0,
					requestBrakeSubject$: new Subject<void>(),
					mutation: createFileMutation,
				})
			).then(res => this.createFileToAddForm(res, file))
		);

		return Promise.all(fileUploadPromises).then(fileToAddForms => {
			const validFileForms = fileToAddForms.filter(Boolean);
			if (validFileForms.length) {
				this.filesAdded.emit(validFileForms);
			}
			return validFileForms.map(f => f.file.url).filter(Boolean);
		});
	}

	private createFileToAddForm(res: IFileUploadEvent, file: File): IAttachedFile | null {
		const filePk: string = res?.event?.body?.data?.createFile?.file?.pk;
		const fileUrl: string = res?.event?.body?.data?.createFile?.file?.file?.url;

		if (filePk && fileUrl) {
			return {
				file: {
					contentType: file.type,
					name: file.name,
					pk: filePk,
					url: fileUrl,
				},
				name: file.name,
				pk: filePk,
			};
		}
		return null;
	}

	triggerFileInput(fileInput: HTMLInputElement): void {
		fileInput.click();
	}

	analyzeExistingImages(): void {
		this.isLoadingState.emit(true);

		const fileUrls = this.attachedFiles()
			.filter(file => file.file.contentType.startsWith('image'))
			.map(file => file.file.url);

		this.performAnalysis(fileUrls, true);
		this.trackPendoService.track('Asset image analysis - existing');
	}

	private convertAssetDataToJson(data: ICmmsAsset | FormGroup): string {
		if (!data) {
			return null;
		}
		if (data instanceof FormGroup) {
			return JSON.stringify(data.getRawValue());
		}

		return JSON.stringify(data);
	}

	private performAnalysis(fileUrls: string[], isExisting: boolean): void {
		if (!fileUrls) {
			this.notify.error(
				this.translateService.instant('no_files_provided_for_image_analysis_error')
			);
			return;
		}
		const existingDataJson = this.convertAssetDataToJson(this.existingData());

		this.imageAnalysisService
			.analyzeImages(fileUrls, isExisting ? existingDataJson : null)
			.subscribe({
				next: (response: IAiAssetAnalysisResponse) => {
					if (response) {
						const formattedResponse = this.formatResponse(response);
						this.analysisCompleted.emit(formattedResponse);
					} else {
						this.notify.error(this.translateService.instant('ai_image_analysis_error'));
					}
					this.isLoadingState.emit(false);
				},
				error: error => {
					this.notify.error(this.translateService.instant('ai_image_analysis_error'));
					this.isLoadingState.emit(false);
				},
			});
	}

	private formatResponse(response: IAiAssetAnalysisResponse): IAiAssetAnalysisResponse {
		const formattedResponse: IAiAssetAnalysisResponse = response;

		formattedResponse.building_component_classification =
			formattedResponse.building_component_table_code
				? [
						this.findBuildingComponentClassification(
							formattedResponse.building_component_table_code
						),
					].filter(Boolean)
				: [];

		formattedResponse.description =
			formattedResponse.description +
			'\n\n\n' +
			this.formatTechicalSpecifications(response.technical_specifications) +
			'\n\n\n' +
			'Labels: ' +
			response?.labels?.join(', ') +
			'\n\n\n';

		return formattedResponse;
	}

	private formatTechicalSpecifications(technicalSpecifications: string): string {
		if (!technicalSpecifications || technicalSpecifications.trim() === '') {
			return '';
		}

		try {
			const parsed = JSON.parse(technicalSpecifications);
			if (!parsed) {
				return '';
			}
			return Object.entries(parsed)
				.map(([key, value]) => `${key}: ${value}`)
				.join('\n');
		} catch (error) {
			console.error('Error parsing technical specifications JSON:', error);
			return '';
		}
	}

	private findBuildingComponentClassification(code: string): IClassification {
		if (!code) {
			return undefined;
		}
		return this.classifications()?.find(c => c.type === 'BCT' && c.pk === code);
	}

	openAiInfoDialog(): MatDialogRef<AiInfoDialogComponent> {
		return this.dialog.open(AiInfoDialogComponent, {
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal'],
		});
	}

	openAiImageTipsDialog(): MatDialogRef<AiImageTipsDialogComponent> {
		return this.dialog.open(AiImageTipsDialogComponent, {
			backdropClass: 'au-modal-backdrop',
			panelClass: ['au-modal'],
		});
	}
}
